import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { readApiError } from '@/services/api';
import { fetchGeneratedDocumentFile, type FileDisposition } from '../services/agreementDocuments';
import type { GeneratedDocument } from '../types/agreementDocuments.types';
import { isJustificationRequired } from '../utils/accessRefusal';
import { generatedDocumentFilename } from '../utils/generatedDocumentDisplay';

const FALLBACK_MESSAGE = 'The document could not be loaded.';

const messageOf = (error: unknown): string =>
    typeof error === 'object' && error !== null && 'data' in error
        ? readApiError((error as { data: unknown }).data).message
        : FALLBACK_MESSAGE;

const saveAs = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
};

interface Viewing {
    document: GeneratedDocument;
    fileUrl: string | null;
    error: string | null;
}

/**
 * Both calls are mutations, so the access refusal watcher sees a justification refusal as it sees
 * one from a query. The viewer's object URL is revoked when it closes, when the tab is left and on unmount.
 */
export function useGeneratedDocumentFile(agreementId: string | undefined, agreementRef: string | undefined, isActive: boolean) {
    const [viewing, setViewing] = useState<Viewing | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const urlRef = useRef<string | null>(null);
    const requestRef = useRef(0);
    const viewMutation = useMutation({ mutationFn: (document: GeneratedDocument) => fetchGeneratedDocumentFile(document.id, 'inline') });
    const downloadMutation = useMutation({
        mutationFn: ({ document, disposition }: { document: GeneratedDocument; disposition: FileDisposition }) =>
            fetchGeneratedDocumentFile(document.id, disposition),
    });

    const releaseUrl = useCallback(() => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
    }, []);

    const closeViewer = useCallback(() => {
        requestRef.current += 1;
        releaseUrl();
        setViewing(null);
        setDownloadError(null);
    }, [releaseUrl]);

    useEffect(() => releaseUrl, [releaseUrl]);
    useEffect(() => {
        if (!isActive) closeViewer();
    }, [isActive, closeViewer]);

    const view = (document: GeneratedDocument) => {
        releaseUrl();
        const request = (requestRef.current += 1);
        setDownloadError(null);
        setViewing({ document, fileUrl: null, error: null });
        viewMutation.mutate(document, {
            onSuccess: (blob) => {
                if (request !== requestRef.current) return;
                urlRef.current = URL.createObjectURL(blob);
                setViewing({ document, fileUrl: urlRef.current, error: null });
            },
            onError: (error) => {
                if (request !== requestRef.current) return;
                if (agreementId && isJustificationRequired(error, agreementId)) {
                    closeViewer();
                    return;
                }
                setViewing({ document, fileUrl: null, error: messageOf(error) });
            },
        });
    };

    const download = (document: GeneratedDocument) => {
        setDownloadError(null);
        downloadMutation.mutate(
            { document, disposition: 'attachment' },
            {
                onSuccess: (blob) =>
                    saveAs(blob, generatedDocumentFilename(agreementRef ?? 'agreement', document.documentType, document.generatedAt ?? document.createdAt)),
                onError: (error) => {
                    if (agreementId && isJustificationRequired(error, agreementId)) return closeViewer();
                    setDownloadError(messageOf(error));
                },
            },
        );
    };

    return {
        viewing,
        isDownloading: downloadMutation.isPending,
        downloadError,
        view,
        download,
        closeViewer,
        dismissDownloadError: () => setDownloadError(null),
    };
}
